import React, { useRef , useEffect , memo, useState, useMemo } from 'react'

const StreetView = memo(({ job }) => {
    const GoogleMapRef = React.useRef(null);
    const [isLoading, setIsLoading] = useState(true);
    const [hasStreetView, setHasStreetView] = useState(true);
    const [error, setError] = useState(null);
    const [loadingStatus, setLoadingStatus] = useState('initializing'); // Track specific loading stages

    // Memoize position to prevent infinite re-renders
    const position = useMemo(() => ({
        lat: job.geoLocation.coordinates[1], 
        lng: job.geoLocation.coordinates[0]
    }), [job.geoLocation.coordinates])
    
    useEffect(() => {
      console.log('🔄 Street View useEffect triggered for position:', position);
      console.log('📊 Effect run at:', new Date().toISOString());
      
      setIsLoading(true);
      setError(null);
      setLoadingStatus('checking-api');
      
      // Store references for cleanup
      let map = null;
      let panorama = null;
      let streetViewService = null;
      let listeners = [];
      
      // Add a small delay to ensure DOM is ready
      const initializeMap = () => {
        // Check if Google Maps API is loaded
        if (!window.google || !window.google.maps) {
          setError('Google Maps API not loaded');
          setIsLoading(false);
          setLoadingStatus('api-error');
          return;
        }

        // Check if container exists and is mounted
        if (!GoogleMapRef.current) {
          console.error('Map container ref is not available');
          setError('Map container not ready');
          setIsLoading(false);
          setLoadingStatus('container-error');
          return;
        }

        try {
          setLoadingStatus('initializing-map');
          console.log('Initializing Google Maps for position:', position);
          
          // Check if the container has proper dimensions
          if (GoogleMapRef.current) {
            const rect = GoogleMapRef.current.getBoundingClientRect();

            if (rect.width === 0 || rect.height === 0) {
              console.warn('⚠️ Map container has zero dimensions!');
            }
          }
          
          map = new window.google.maps.Map(GoogleMapRef.current, {
            center: position,
            zoom: 14
          });

          setLoadingStatus('creating-panorama');
          console.log('Creating Street View panorama...');
      
          panorama = new window.google.maps.StreetViewPanorama(
            GoogleMapRef.current,
            {
              position: position,
              pov: { heading: 165, pitch: 0 },
              zoom: 1,
              addressControl: false,
              fullscreenControl: false,
              showRoadLabels: false,
              disableDefaultUI: true,     // Disable all default UI elements
              linksControl: false,        // Disable navigation arrows
              panControl: false,          // Disable pan control
              zoomControl: false,         // Disable zoom control
              clickToGo: false            // Prevent moving by clicking on the map
            }
          );

          // Track when panorama is created
          console.log('Panorama created, setting up event listeners...');
          setLoadingStatus('setting-up-listeners');

          // Add event listeners for panorama status and store references for cleanup
          const statusListener = panorama.addListener('status_changed', () => {
            const status = panorama.getStatus();
            console.log('Panorama status changed:', status);
            setLoadingStatus(`panorama-status-${status.toLowerCase()}`);
            
            if (status === 'OK') {
              setIsLoading(false);
              setHasStreetView(true);
              setLoadingStatus('loaded');
            } else if (status === 'ZERO_RESULTS') {
              setIsLoading(false);
              setHasStreetView(false);
              setError('No Street View available for this location');
              setLoadingStatus('no-results');
            }
          });
          listeners.push(statusListener);

          // Track when panorama becomes visible
          const visibilityListener = panorama.addListener('visible_changed', () => {
            const isVisible = panorama.getVisible();
            console.log('Panorama visibility changed:', isVisible);
            if (isVisible) {
              setLoadingStatus('visible');
            }
          });
          listeners.push(visibilityListener);

          // Track when panorama position changes (indicates successful load)
          const positionListener = panorama.addListener('position_changed', () => {
            const newPosition = panorama.getPosition();
            console.log('Panorama position changed:', newPosition?.toString());
            setLoadingStatus('position-set');
          });
          listeners.push(positionListener);

          panorama.setVisible(true);
          setLoadingStatus('requesting-street-view');
          console.log('Requesting Street View data from Google...');

          streetViewService = new window.google.maps.StreetViewService();
          
          // Add timeout for Street View service request
          const requestStartTime = Date.now();
          
          streetViewService.getPanorama(
            { location: position, radius: 50, source: window.google.maps.StreetViewSource.OUTDOOR },
            (data, status) => {
              const requestDuration = Date.now() - requestStartTime;
              console.log(`Street View Service Response (${requestDuration}ms):`, { data, status });
              setLoadingStatus(`service-response-${status.toLowerCase()}`);
              
              if (status === window.google.maps.StreetViewStatus.OK) {
                console.log('Street View data found successfully', data);
                setLoadingStatus('setting-panorama');
                
                panorama.setPano(data.location.pano);
                panorama.setPov({
                    heading: 265,
                    pitch: 0
                });
                panorama.setVisible(true);
                setIsLoading(false);
                setHasStreetView(true);
                setLoadingStatus('complete');
                
                console.log('Street View successfully loaded and configured');
              } else {
                console.error("Street View data not found for this location. Status:", status);
                setIsLoading(false);
                setHasStreetView(false);
                setLoadingStatus(`failed-${status.toLowerCase()}`);
                
                // Set specific error messages based on status
                switch(status) {
                  case window.google.maps.StreetViewStatus.ZERO_RESULTS:
                    setError('No Street View imagery available for this location');
                    break;
                  case window.google.maps.StreetViewStatus.UNKNOWN_ERROR:
                    setError('An unknown error occurred while loading Street View');
                    break;
                  default:
                    setError(`Street View error: ${status}`);
                }
              }
            }
          );
      
          map.setStreetView(panorama);
          console.log('Map and Street View connection established');
          
        } catch (err) {
          console.error('Error initializing Street View:', err);
          setError('Failed to initialize Street View');
          setIsLoading(false);
          setLoadingStatus('initialization-error');
        }
      }; // End of initializeMap function
      
      // Use setTimeout to ensure DOM is ready
      const timeoutId = setTimeout(initializeMap, 100);
      
      // Return cleanup function
      return () => {
        clearTimeout(timeoutId);
        console.log('🧹 Cleaning up Street View resources...');
        
        // Remove all event listeners
        listeners.forEach(listener => {
          if (listener && typeof listener.remove === 'function') {
            listener.remove();
          }
        });
        
        // Clear panorama
        if (panorama) {
          panorama.setVisible(false);
          panorama = null;
        }
        
        // Clear map
        if (map) {
          map = null;
        }
        
        // Clear service
        streetViewService = null;
        
        console.log('✅ Street View cleanup completed');
      };
    }, [position]);
  
    return (
      <div className='offer-googlestreetview' style={{
        position: 'relative',
        width: '100%',
        height: '100%', // Changed from fixed 400px to 100% to adapt to parent
        minHeight: '200px', // Reduced minimum height for grid cards
        overflow: 'hidden'
      }}>
        {isLoading && (
          <div className="loading-overlay" style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}>
            <div>Loading Street View...</div>
            <div style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
              Status: {loadingStatus}
            </div>
          </div>
        )}
        {error && (
          <div className="error-overlay" style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(255, 0, 0, 0.1)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            color: '#d32f2f'
          }}>
            <div>{error}</div>
            <div style={{ fontSize: '12px', marginTop: '5px' }}>
              Status: {loadingStatus}
            </div>
          </div>
        )}
        <div 
          ref={GoogleMapRef} 
          style={{ 
            width: '100%', 
            height: '100%',
            minHeight: '200px', // Reduced from 300px
            borderRadius: '8px 8px 0 0' // Only round top corners to match card design
          }}
        ></div>
      </div>
    );
  });
  
  export default StreetView; 